import type { ChangeEvent } from "react";
import { useTranslation } from "react-i18next";
import {
  useDiscoverActions,
  useDiscoverCities,
  useDiscoverContext,
  useDiscoverFilters,
  useDiscoverMeta,
} from "../state/discover-context";
import { DEFAULT_TAB } from "../state/discover-reducer";
import { useInViewItems } from "../hooks/use-in-view-items";
import { countsState } from "../lib/counts-state";
import { CityFilter } from "./city-filter";
import { DiscoverCard } from "./discover-card";
import { DiscoverShelves } from "./discover-shelves";

import { Icon } from "@majourneys/icons";
const VISIBILITY_OPTIONS = [
  { id: "all" as const, labelKey: "discover.visibility.all" },
  { id: "bookable" as const, labelKey: "discover.visibility.bookable" },
  { id: "explore" as const, labelKey: "discover.visibility.explore" },
];

/**
 * Placeholder cards in the shape of the real ones. No text: the listing header
 * already says "Loading activities…", and a screen reader should hear that
 * once, not six empty cards.
 */
function DiscoverSkeletonGrid() {
  return (
    <div className="mj-discover-browser__grid mj-discover-skeletons" aria-hidden="true">
      {Array.from({ length: 6 }, (_, i) => (
        <div key={i} className="mj-discover-skeleton">
          <div className="mj-discover-skeleton__media" />
          <div className="mj-discover-skeleton__line" />
          <div className="mj-discover-skeleton__line is-short" />
        </div>
      ))}
    </div>
  );
}

/**
 * The tab is empty. When the words typed match something on another tab, say
 * where and take the guest there in one tap: "briganti" on Things to do used
 * to end on "No matches" while Briganti sat under Restaurants.
 */
function DiscoverEmpty() {
  const { t } = useTranslation();
  const { state } = useDiscoverContext();
  const { setTab, setSearch } = useDiscoverActions();
  const filters = useDiscoverFilters();
  const query = state.searchQuery.trim();
  const active = filters.find((f) => f.id === state.activeTab);
  const tabName = (f: (typeof filters)[number]) => (f.labelKey ? t(f.labelKey) : f.label);
  // The named tabs first: "For you 1" beside "Restaurants 1" offers the same
  // row twice. For you only when no named tab has it.
  const others = query ? filters.filter((f) => f.id !== state.activeTab && f.count > 0) : [];
  const named = others.filter((f) => f.id !== DEFAULT_TAB);
  const elsewhere = named.length > 0 ? named : others;

  if (!query) {
    return (
      <div className="mj-discover-browser__empty">
        <Icon name="ri-search-eye-line" aria-hidden="true" />
        <p>{t("discover.no_matches")}</p>
      </div>
    );
  }

  return (
    <div className="mj-discover-browser__empty" role="status">
      <Icon name="ri-search-eye-line" aria-hidden="true" />
      <p className="mj-discover-browser__empty-title">
        {elsewhere.length > 0 && active
          ? t("discover.no_matches_in_tab", { tab: tabName(active), query })
          : t("discover.no_matches_for", { query })}
      </p>
      {elsewhere.length > 0 ? (
        <>
          <p>{t("discover.matches_elsewhere")}</p>
          <div className="mj-discover-browser__empty-tabs">
            {elsewhere.map((f) => (
              <button
                key={f.id}
                type="button"
                className="mj-press mj-discover-browser__tab"
                onClick={() => setTab(f.id)}
              >
                <span>{tabName(f)}</span>
                <strong>{f.count}</strong>
              </button>
            ))}
          </div>
        </>
      ) : null}
      <button type="button" className="mj-discover-browser__empty-clear" onClick={() => setSearch("")}>
        {t("discover.clear_search")}
      </button>
    </div>
  );
}

export function DiscoverBrowser() {
  const { t } = useTranslation();
  const { state, items, isLoadingItems, itemsError } = useDiscoverContext();
  // Tab counts read off an empty catalogue would all say 0 while it loads.
  const counts = countsState(items.length, isLoadingItems, itemsError);
  const { setTab, setVisibility, setSearch, setCity } = useDiscoverActions();
  const filters = useDiscoverFilters();
  const cities = useDiscoverCities();
  const { heroLabel } = useDiscoverMeta();
  const { items: filtered, active: inView } = useInViewItems();

  const activeFilter = filters.find((f) => f.id === state.activeTab);

  // The themed-shelf landing replaces the flat grid only on the default
  // "For you" view with no search and the map zoomed out (no viewport
  // narrowing). Any tab switch, search, or map zoom falls back to the precise
  // flat grid below.
  const showShelves =
    state.activeTab === DEFAULT_TAB && state.searchQuery.trim() === "" && !inView;

  return (
    <section className="mj-discover-browser mj-dk-frame" aria-label={t("discover.browser")}>
      <div className="mj-discover-browser__topline">
        <span className="mj-discover-browser__eyebrow">{t("discover.hero_label", { defaultValue: heroLabel })}</span>
      </div>

      <div className="mj-discover-browser__toolbar">
        <CityFilter
          cities={cities}
          selectedSlug={state.selectedCitySlug}
          onSelect={(slug) => setCity(slug)}
        />
        <label className="mj-discover-browser__search mj-field-focus">
          <Icon name="ri-search-line" aria-hidden="true" />
          <input
            type="search"
            aria-label={t("discover.search_placeholder")}
            placeholder={t("discover.search_placeholder")}
            value={state.searchQuery}
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              setSearch(event.target.value)
            }
          />
        </label>
      </div>

      <div className="mj-discover-browser__subfilters">
        {VISIBILITY_OPTIONS.map((option) => {
          const isActive = state.visibility === option.id;
          return (
            <button
              key={option.id}
              type="button"
              className={`mj-press mj-discover-browser__subfilter${isActive ? " is-active" : ""}`}
              onClick={() => setVisibility(option.id)}
              aria-pressed={isActive}
            >
              {t(option.labelKey)}
            </button>
          );
        })}
      </div>

      <div className="mj-discover-browser__tabs">
        {filters.map((filter) => {
          const isActive = state.activeTab === filter.id;
          return (
            <button
              key={filter.id}
              type="button"
              className={`mj-press mj-discover-browser__tab${isActive ? " is-active" : ""}`}
              onClick={() => setTab(filter.id)}
              aria-pressed={isActive}
            >
              <span>{filter.labelKey ? t(filter.labelKey) : filter.label}</span>
              {counts === "loading" ? (
                <span className="mj-discover-bone" style={{ width: 14, height: 8 }} aria-hidden="true" />
              ) : counts === "failed" ? null : (
                <strong>{filter.count}</strong>
              )}
            </button>
          );
        })}
      </div>

      <div className="mj-discover-browser__listing">
        <header className="mj-discover-browser__listing-header">
          <h2>{activeFilter?.labelKey ? t(activeFilter.labelKey) : (activeFilter?.label ?? t("discover.for_you"))}</h2>
          <p>
            {isLoadingItems
              ? t("discover.loading_activities")
              : counts === "failed"
                ? null
                : inView
                ? t("discover.picks_visible_short", { count: filtered.length })
                : t("discover.picks_visible", { count: filtered.length })}
          </p>
        </header>

        {counts === "failed" ? (
          // The raw error is for us, not the guest, and it is English.
          <p className="mj-discover-browser__error">{t("discover.load_failed")}</p>
        ) : isLoadingItems && filtered.length === 0 ? (
          // The index is one request for the whole catalogue, so the first
          // second has no items in it. Without this branch the empty state
          // fires and the page reads "No matches" while it is still loading —
          // a dead end, next to a header that says the opposite.
          <DiscoverSkeletonGrid />
        ) : filtered.length === 0 ? (
          <DiscoverEmpty />
        ) : showShelves ? (
          <DiscoverShelves />
        ) : (
          <div className="mj-discover-browser__grid">
            {filtered.map((item) => (
              <DiscoverCard key={item.id} item={item} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
